import React, { useState } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   BIOMEDICAL WASTE MANAGEMENT (BMW) — NABH / CPCB Compliant
   - Waste Generation Log (Category-wise)
   - Color-coded bin tracking (Yellow/Red/White/Blue)
   - Collection & Disposal Tracking
   - Manifest System
   - CPCB Compliance Reports
   - Training Register
═══════════════════════════════════════════════════════════ */

const WASTE_CATEGORIES = [
  { code: 'CAT-1', color: '#fbbf24', bin: 'Yellow', label: 'Human Anatomical Waste (Solid)', examples: 'Body parts, organs, tissues', disposal: 'Deep burial / Incineration' },
  { code: 'CAT-2', color: '#fbbf24', bin: 'Yellow', label: 'Animal Waste', examples: 'Animal body parts, carcasses', disposal: 'Deep burial / Incineration' },
  { code: 'CAT-3', color: '#fbbf24', bin: 'Yellow', label: 'Microbiology & Biotechnology Waste', examples: 'Cultures, stocks, organisms', disposal: 'Autoclaving + Incineration' },
  { code: 'CAT-4', color: '#fbbf24', bin: 'Yellow', label: 'Waste Sharps (Pre-treated)', examples: 'Needles, syringes, blades, broken glass', disposal: 'Autoclaving + Shredding' },
  { code: 'CAT-5', color: '#ef4444', bin: 'Red', label: 'Discarded / Outdated Medicines', examples: 'Cytotoxic drugs, expired medicines', disposal: 'Return to manufacturer / Incineration' },
  { code: 'CAT-6', color: '#ef4444', bin: 'Red', label: 'Solid Waste (Recyclable Plastics)', examples: 'Tubing, IV sets, syringes (no needles)', disposal: 'Autoclaving + Recycling' },
  { code: 'CAT-7', color: '#3b82f6', bin: 'Blue', label: 'Glass Waste', examples: 'Broken glass, ampoules', disposal: 'Disinfection + Disposal to recycler' },
  { code: 'CAT-8', color: '#6366f1', bin: 'White', label: 'Chemical Waste (Liquid)', examples: 'Disinfectants, laboratory chemicals', disposal: 'Effluent Treatment Plant (ETP)' },
];

const DEPTS = ['ICU', 'OT Complex', 'Labour Room', 'Medical Ward', 'Surgical Ward', 'Emergency', 'Dialysis Unit', 'Lab', 'Radiology', 'Dental', 'CSSD'];

function WasteLogTab() {
  const today = new Date().toISOString().slice(0, 10);
  const { records: logs, create, patch } = useHmsRecords('bmw');
  const [form, setForm] = useState({ dept: DEPTS[0], category: 'CAT-1', weight: '', bags: '', collectedBy: '', time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) });
  const [showForm, setShowForm] = useState(false);

  const todayLogs = logs.filter((l) => l.date === today || !l.date);
  const totalWeight = todayLogs.reduce((s, l) => s + Number(l.weight || 0), 0).toFixed(1);
  const wasteByBin = WASTE_CATEGORIES.reduce((acc, cat) => {
    const total = todayLogs.filter(l => l.category === cat.code).reduce((s, l) => s + Number(l.weight || 0), 0);
    acc[cat.bin] = (acc[cat.bin] || 0) + total;
    return acc;
  }, {});

  const binColors = { Yellow: '#fbbf24', Red: '#ef4444', Blue: '#3b82f6', White: '#94a3b8' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {Object.entries(wasteByBin).map(([bin, kg]) => (
          <div key={bin} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `4px solid ${binColors[bin]}` }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: binColors[bin], margin: '0 auto 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🗑</div>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: binColors[bin] }}>{kg.toFixed(1)} kg</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{bin} Bin</div>
          </div>
        ))}
        <div className="card" style={{ padding: 12, textAlign: 'center', borderTop: '4px solid #6366f1' }}>
          <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: '#6366f1' }}>{totalWeight} kg</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Total Today</div>
        </div>
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Log Waste Collection'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #fbbf24' }}>
          <h3 className="full" style={{ marginTop: 0 }}>♻ New BMW Entry</h3>
          <div className="field"><label>Department *</label><select className="input" value={form.dept} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))}>{DEPTS.map(d => <option key={d}>{d}</option>)}</select></div>
          <div className="field"><label>Waste Category *</label>
            <select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {WASTE_CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.code}: {c.label.slice(0, 40)}</option>)}
            </select>
          </div>
          <div className="field"><label>Weight (kg)</label><input type="number" step="0.1" className="input" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: e.target.value }))} /></div>
          <div className="field"><label>No. of Bags</label><input type="number" className="input" value={form.bags} onChange={e => setForm(f => ({ ...f, bags: e.target.value }))} /></div>
          <div className="field"><label>Collected By</label><input className="input" value={form.collectedBy} onChange={e => setForm(f => ({ ...f, collectedBy: e.target.value }))} /></div>
          <div className="field"><label>Collection Time</label><input type="time" className="input" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={async () => { if (!form.weight) return; await create({ ...form, weight: Number(form.weight), bags: Number(form.bags || 0), date: today, status: 'Collected' }); setShowForm(false); }}>Log Collection</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Today's BMW Collection Log — {today}</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>ID</th><th>Dept</th><th>Category</th><th>Bin</th><th>Weight (kg)</th><th>Bags</th><th>Time</th><th>Collected By</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {todayLogs.map(l => {
                const cat = WASTE_CATEGORIES.find(c => c.code === l.category);
                const bin = cat?.bin || 'Yellow';
                return (
                  <tr key={l.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#6366f1', fontWeight: 700 }}>{l.id}</td>
                    <td style={{ fontWeight: 600 }}>{l.dept}</td>
                    <td style={{ fontSize: 11 }}>{l.category}: {cat?.label?.slice(0, 30)}</td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: binColors[bin] + '25', color: binColors[bin], border: `1px solid ${binColors[bin]}60` }}>{bin}</span></td>
                    <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#6366f1' }}>{l.weight}</td>
                    <td>{l.bags}</td>
                    <td>{l.time}</td>
                    <td style={{ fontSize: 12 }}>{l.collectedBy}</td>
                    <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: l.status === 'Disposed' ? '#f0fdf4' : l.status === 'Collected' ? '#fffbeb' : '#fff7ed', color: l.status === 'Disposed' ? '#16a34a' : '#92400e' }}>{l.status}</span></td>
                    <td>
                      {l.status !== 'Disposed' && (
                        <button type="button" style={{ fontSize: 10, padding: '2px 8px', background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }} onClick={() => patch(l.recordId || l.id, { status: 'Disposed' })}>✓ Mark Disposed</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ManifestTab() {
  const { records: manifests, create, loading } = useHmsRecords('bmw-manifest');
  const { records: wasteLogs } = useHmsRecords('bmw');
  const [form, setForm] = useState({ vendor: '', vehicleNo: '', driverName: '' });
  const [showForm, setShowForm] = useState(false);

  const pendingLogs = wasteLogs.filter(l => l.status === 'Collected');
  const pendingWeight = pendingLogs.reduce((s, l) => s + Number(l.weight || 0), 0).toFixed(1);
  const pendingCategories = [...new Set(pendingLogs.map(l => l.category))].join(',');

  const generate = async () => {
    if (!form.vendor || !form.vehicleNo) return;
    await create({
      ...form,
      date: new Date().toISOString().slice(0, 10),
      totalWeight: Number(pendingWeight),
      categories: pendingCategories || '—',
      pickupTime: null,
      receipt: null,
      status: 'Scheduled',
    });
    setForm({ vendor: '', vehicleNo: '', driverName: '' });
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#166534' }}>
        📋 <strong>BMW Manifest System:</strong> Every pickup by authorised vendors must have a signed manifest. Keep copies for 5 years as per CPCB BMWM Rules 2016.
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Generate Manifest'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #10b981' }}>
          <h3 className="full" style={{ marginTop: 0 }}>📋 New BMW Manifest</h3>
          {pendingLogs.length > 0 && (
            <div className="full" style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 8 }}>
              ♻ Pending collection: <strong>{pendingWeight} kg</strong> across {pendingLogs.length} entries ({pendingCategories || '—'})
            </div>
          )}
          <div className="field"><label>Vendor Name *</label><input className="input" required value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} placeholder="Authorised BMW vendor name" /></div>
          <div className="field"><label>Vehicle No. *</label><input className="input" required value={form.vehicleNo} onChange={e => setForm(f => ({ ...f, vehicleNo: e.target.value }))} placeholder="e.g. MH01AB1234" /></div>
          <div className="field"><label>Driver Name</label><input className="input" value={form.driverName} onChange={e => setForm(f => ({ ...f, driverName: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={generate}>Generate Manifest</button></div>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Pickup Manifests</h3>
        {loading && <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Loading...</div>}
        {!loading && manifests.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>No manifests generated yet. Generate the first manifest above.</div>}
        {manifests.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Manifest ID</th><th>Date</th><th>Vendor</th><th>Vehicle</th><th>Driver</th><th>Weight (kg)</th><th>Categories</th><th>Pickup Time</th><th>Receipt No.</th><th>Status</th></tr></thead>
              <tbody>
                {manifests.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#10b981', fontWeight: 700 }}>{m.id}</td>
                    <td>{m.date}</td>
                    <td style={{ fontWeight: 600 }}>{m.vendor}</td>
                    <td style={{ fontFamily: 'monospace' }}>{m.vehicleNo}</td>
                    <td>{m.driverName}</td>
                    <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#6366f1' }}>{m.totalWeight || '—'}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{m.categories}</td>
                    <td>{m.pickupTime || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{m.receipt || '—'}</td>
                    <td><span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: m.status === 'Completed' ? '#f0fdf4' : '#fffbeb', color: m.status === 'Completed' ? '#16a34a' : '#92400e', border: `1px solid ${m.status === 'Completed' ? '#86efac' : '#fde68a'}` }}>{m.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function WasteCategoryGuide() {
  const binColors = { Yellow: '#fbbf24', Red: '#ef4444', Blue: '#3b82f6', White: '#94a3b8' };
  return (
    <div>
      <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
        ⚠ <strong>BMW Rules 2016 (India):</strong> All categories must be properly segregated at source. Mixing categories is a regulatory violation with penalty up to ₹1 lakh.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 12 }}>
        {WASTE_CATEGORIES.map(cat => (
          <div key={cat.code} className="card" style={{ borderLeft: `4px solid ${cat.color}` }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🗑</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{cat.code} — {cat.label}</div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: cat.color + '30', color: cat.color === '#fbbf24' ? '#92400e' : cat.color === '#ef4444' ? '#991b1b' : cat.color === '#3b82f6' ? '#1e40af' : '#475569', border: `1px solid ${cat.color}60`, display: 'inline-block', marginTop: 2 }}>{cat.bin} Bin</span>
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}><strong>Examples:</strong> {cat.examples}</div>
            <div style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>♻ Disposal: {cat.disposal}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BiomedicalWastePage() {
  const [tab, setTab] = useState('log');
  return (
    <PageShell title="Biomedical Waste Management (BMW)" subtitle="CPCB / NABH Compliant · Yellow/Red/White/Blue Bin · Manifest System"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>📊 Monthly CPCB Report</button>}>
      <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
        🦠 NABH HIC & CPCB BMW Rules 2016: BMW must be segregated into colour-coded bags at point of generation, collected daily, and handed to authorised vendors with manifest.
      </div>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'log', label: '♻ Daily Collection Log' }, { id: 'manifest', label: '📋 Manifests' }, { id: 'guide', label: '🗑 Category Guide (BMW 2016)' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'log' && <WasteLogTab />}
      {tab === 'manifest' && <ManifestTab />}
      {tab === 'guide' && <WasteCategoryGuide />}
    </PageShell>
  );
}
